import axios from 'axios';
import config from 'constants/apiConfig';
import { parseError } from 'utils/parseTypedErrorMessage';
import { normalizeActivityLogSummaryFilters } from './activityLogKeys';
import type {
  ActivityLog,
  ActivityLogEntry,
  ActivityLogSummaryFilters,
  ActivityLogSummaryItem,
  ActivityLogWithEntries,
  CreateActivityLogEntryRequest,
  CreateActivityLogRequest,
  UpdateActivityLogEntryRequest,
  UpdateActivityLogRequest,
} from './activityLogTypes';

const toActivityLogError = (error: unknown, fallbackMessage: string) => {
  return new Error(parseError(error) ?? fallbackMessage);
};

export const fetchActivityLogs = async (
  incidentId: string,
  signal?: AbortSignal
) => {
  try {
    const { data } = await axios.get<ActivityLog[]>(
      config.apiGateway.activityLogsIncident(incidentId),
      { signal }
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to load activity logs.');
  }
};

export const fetchActivityLog = async (
  incidentId: string,
  logDate: string,
  signal?: AbortSignal
) => {
  try {
    const { data } = await axios.get<ActivityLogWithEntries>(
      config.apiGateway.activityLogsDetail(incidentId, logDate),
      { signal }
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to load the activity log.');
  }
};

export const fetchActivityLogSummary = async (
  incidentId: string,
  filters: ActivityLogSummaryFilters = {},
  signal?: AbortSignal
) => {
  try {
    const { data } = await axios.get<ActivityLogSummaryItem[]>(
      config.apiGateway.activityLogsSummary(incidentId),
      {
        params: normalizeActivityLogSummaryFilters(filters),
        signal,
      }
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to load the activity log summary.');
  }
};

export const createActivityLog = async (
  incidentId: string,
  logDate: string,
  request: CreateActivityLogRequest
) => {
  try {
    const { data } = await axios.post<ActivityLog>(
      config.apiGateway.activityLogsDetail(incidentId, logDate),
      request
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to create the activity log.');
  }
};

export const updateActivityLog = async (
  incidentId: string,
  logDate: string,
  request: UpdateActivityLogRequest
) => {
  try {
    const { data } = await axios.patch<ActivityLog>(
      config.apiGateway.activityLogsDetail(incidentId, logDate),
      request
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to update the activity log.');
  }
};

export const createActivityLogEntry = async (
  incidentId: string,
  logDate: string,
  request: CreateActivityLogEntryRequest
) => {
  try {
    const { data } = await axios.post<ActivityLogEntry>(
      config.apiGateway.activityLogsEntries(incidentId, logDate),
      request
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to create the activity log entry.');
  }
};

export const updateActivityLogEntry = async (
  incidentId: string,
  logDate: string,
  entryId: string,
  request: UpdateActivityLogEntryRequest
) => {
  try {
    const { data } = await axios.patch<ActivityLogEntry>(
      config.apiGateway.activityLogsEntry(incidentId, logDate, entryId),
      request
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to update the activity log entry.');
  }
};

export const deleteActivityLogEntry = async (
  incidentId: string,
  logDate: string,
  entryId: string
) => {
  try {
    const { data } = await axios.delete<ActivityLogEntry>(
      config.apiGateway.activityLogsEntry(incidentId, logDate, entryId)
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to delete the activity log entry.');
  }
};

export const submitActivityLog = async (
  incidentId: string,
  logDate: string,
  timezone?: string
) => {
  try {
    const { data } = await axios.post<ActivityLog>(
      config.apiGateway.activityLogsSubmit(incidentId, logDate),
      undefined,
      { params: timezone ? { timezone } : undefined }
    );
    return data;
  } catch (error) {
    throw toActivityLogError(error, 'Failed to submit the activity log.');
  }
};

export const fetchActivityLogPreview = async (
  incidentId: string,
  logDate: string,
  timezone?: string
) => {
  try {
    const { data } = await axios.get<Blob>(
      config.apiGateway.activityLogsPreview(incidentId, logDate),
      {
        responseType: 'blob',
        params: timezone ? { timezone } : undefined,
      }
    );
    return data;
  } catch (error) {
    throw toActivityLogError(
      error,
      'Failed to load the activity log preview PDF.'
    );
  }
};
